<template>
  <div ref="root" class="language-dropdown" :class="{ 'is-inline': inline }" @keydown.esc.stop.prevent="close(true)" @focusout="onFocusOut">
    <button
      :id="id"
      ref="trigger"
      type="button"
      class="language-trigger"
      :aria-label="selected.name"
      :aria-expanded="open"
      :aria-controls="`${id}-options`"
      @click="open = !open"
      @keydown.down.prevent="openOptions"
      @keydown.up.prevent="openOptions"
    >
      <span :class="`fi fi-${selected.label}`" aria-hidden="true"></span>
      <span>{{ inline ? selected.name : selected.code === 'bz' ? 'PT' : selected.code.toUpperCase() }}</span>
      <svg class="language-chevron" :class="{ 'is-open': open }" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="m5 7.5 5 5 5-5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>
    <div v-if="open" :id="`${id}-options`" ref="options" class="language-options" :aria-labelledby="id">
      <button
        v-for="(language, index) in languages"
        :key="language.code"
        type="button"
        class="language-option"
        :aria-pressed="language.code === modelValue"
        @click="select(language.code)"
        @keydown.down.prevent="focusOption(index + 1)"
        @keydown.up.prevent="focusOption(index - 1)"
        @keydown.home.prevent="focusOption(0)"
        @keydown.end.prevent="focusOption(languages.length - 1)"
      >
        <span :class="`fi fi-${language.label}`" aria-hidden="true"></span>
        <span>{{ language.name }}</span>
        <svg v-if="language.code === modelValue" class="language-check" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="m4 10 4 4 8-8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, nextTick, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'

const props = defineProps({
  id: { type: String, required: true },
  modelValue: { type: String, required: true },
  languages: { type: Array, required: true },
  inline: Boolean
})
const emit = defineEmits(['update:modelValue'])
const root = ref(null)
const trigger = ref(null)
const options = ref(null)
const open = ref(false)
const selected = computed(() => props.languages.find(language => language.code === props.modelValue) || props.languages[0])

function close (restoreFocus = false) {
  open.value = false
  if (restoreFocus) trigger.value?.focus()
}

function focusOption (index) {
  const buttons = options.value?.querySelectorAll('button')
  buttons?.[(index + buttons.length) % buttons.length]?.focus()
}

async function openOptions () {
  open.value = true
  await nextTick()
  focusOption(Math.max(0, props.languages.findIndex(language => language.code === props.modelValue)))
}

function select (code) {
  emit('update:modelValue', code)
  close(true)
}

function onFocusOut (event) {
  if (!root.value?.contains(event.relatedTarget)) close()
}

onClickOutside(root, () => close())
</script>

<style scoped>
.language-dropdown { position: relative; flex-shrink: 0; text-align: left; }
.language-trigger, .language-option {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-family: var(--font-ui);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.language-trigger {
  min-height: 40px;
  padding: 8px 10px;
  border: 1px solid #ffffff26;
  border-radius: 10px;
  background: #ffffff0d;
  transition: background-color .15s, border-color .15s;
}
.language-trigger:hover, .language-trigger[aria-expanded="true"] { background: #ffffff1a; border-color: #ffffff4d; }
.fi { flex-shrink: 0; width: 24px; height: 18px; border-radius: 3px; background-size: cover; }
.language-chevron { width: 16px; height: 16px; margin-left: auto; transition: transform .15s; }
.language-chevron.is-open { transform: rotate(180deg); }
.language-options {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1;
  width: 200px;
  padding: 6px;
  border: 1px solid #ffffff26;
  border-radius: 14px;
  background: #101b3d;
  box-shadow: 0 12px 32px #0006;
}
.language-option { width: 100%; min-height: 40px; padding: 8px 10px; border-radius: 8px; background: transparent; }
.language-option:hover { background: #ffffff14; }
.language-option[aria-pressed="true"] { color: #ffcd00; background: #ffcd0014; }
.language-check { width: 18px; height: 18px; margin-left: auto; flex-shrink: 0; }
button:focus-visible { outline: 2px solid #ffcd00; outline-offset: 2px; }
.is-inline .language-trigger { width: 100%; min-height: 44px; }
.is-inline .language-options { position: static; width: 100%; margin-top: 8px; }
.is-inline .language-option { min-height: 44px; }
@media (prefers-reduced-motion: reduce) { .language-trigger, .language-chevron { transition: none; } }
</style>
